import {PlatformCommand} from '@shared/commands/command-base';
import {Api} from '@shared/constants/common-field.constants';
import {browserZone} from '@shared/helpers/time-zone-mismatch';

/** How long a meeting scheduled from a chat runs until the user changes it in the dialog. */
export const SCHEDULE_MEETING_MINUTES = 30;

/** A guest the dialog preselects: it matches a company contact by the user's email and company. */
export interface ScheduleMeetingGuest {
  user: {email: string, firstName?: string, lastName?: string, companyId?: string};
}

/**
 * The chat members a meeting scheduled from the chat invites (ITP-E-208-04): the members who have not left, of the user's
 * own company — another company's members are not invited (D-08), as Start a Meet's are not — each once, and not the
 * user, who is the event's organiser. A member with no company on record counts as the user's, as the server reads them.
 */
export function scheduleMeetingGuests(conversation: any, me: {email?: string, companyId?: string} | null | undefined)
  : ScheduleMeetingGuest[] {
  const myEmail = (me?.email || '').toLowerCase();
  const myCompany = (me?.companyId || '').toLowerCase();
  const seen = new Set<string>();
  return (conversation?.roles || [])
    .filter(role => role?.user?.email && !role.hasLeft)
    .filter(role => role.user.email.toLowerCase() !== myEmail)
    .filter(role => !role.user.companyId || role.user.companyId.toLowerCase() === myCompany)
    .filter(role => !seen.has(role.user.email.toLowerCase()) && !!seen.add(role.user.email.toLowerCase()))
    .map(role => ({
      user: {
        email: role.user.email,
        firstName: role.user.firstName,
        lastName: role.user.lastName,
        companyId: role.user.companyId || me?.companyId
      }
    }));
}

/**
 * Where the dialog opens, as the calendar opens on the slot clicked: the next half hour from `now`, for
 * {@link SCHEDULE_MEETING_MINUTES} minutes. The user picks the real time in the dialog.
 */
export function scheduleMeetingSlot(now: Date = new Date()): {start: Date, end: Date} {
  const start = new Date(now.getTime());
  start.setSeconds(0, 0);
  start.setMinutes(start.getMinutes() < 30 ? 30 : 60);
  return {start, end: new Date(start.getTime() + SCHEDULE_MEETING_MINUTES * 60000)};
}

/**
 * The calendar's meeting dialog for a chat (ITP-E-348-02): `CreateCalendarEventCommand`, which the server answers with
 * "New meeting" opened on `slot` in the profile's zone, else the browser's, the guests ticked in Participants and the chat's
 * id, which locks the Orbitax Meet on. Save makes the event's Meet, binds it to the chat and posts the event's card there.
 */
export function scheduleMeetingCommand(conversationId: string, guests: ScheduleMeetingGuest[],
                                       slot: {start: Date, end: Date}): PlatformCommand {
  const command = new PlatformCommand('CreateCalendarEventCommand', Api.Scheduler);
  command.fieldValues = {
    InitialStartTime: slot.start.toISOString(),
    InitialEndTime: slot.end.toISOString(),
    InitialGuests: guests.map(guest => guest.user.email),
    InitialBrowserZone: browserZone(),
    ConversationId: conversationId
  };
  return command;
}
